import type { ReactNode } from 'react'

export type CalloutTone = 'info' | 'danger' | 'success' | 'warning'

// Each tone on its `-subtle` ground. The text stays ink on the success and warning
// tones: WyseHub's green and amber are too light on their tints for body copy, and the
// border and background already say which tone it is.
const TONES: Readonly<Record<CalloutTone, string>> = {
  info: 'border-line bg-surface-muted text-ink',
  danger: 'border-danger/40 bg-danger-subtle text-danger',
  success: 'border-success/40 bg-success-subtle text-ink',
  warning: 'border-warning/40 bg-warning-subtle text-ink',
}

export interface CalloutProps {
  readonly tone?: CalloutTone
  readonly title?: string
  readonly children: ReactNode
  readonly className?: string
}

/**
 * A short message block. Error tones announce themselves: an error a screen reader
 * never hears is not reported.
 */
export function Callout({
  tone = 'info',
  title,
  children,
  className,
}: CalloutProps) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={`rounded-lg border px-4 py-3 text-sm ${TONES[tone]} ${className ?? ''}`.trim()}
    >
      {title ? <p className="mb-1 font-semibold">{title}</p> : null}
      {children}
    </div>
  )
}
